import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildPath } from './lib/tenant';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
    Container, Header, Loader, Dimmer, Message, Segment, Icon, Button,
} from 'semantic-ui-react';
import { getTransferStatus, acceptTransfer } from './lib/tickets';
import { useAuthStore } from './lib/authStore';
import { t } from './locales';

function AcceptTransfer() {
    const { token } = useParams();
    const navigate = useNavigate();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const currentUser = useAuthStore((s) => s.user);
    const [accepted, setAccepted] = useState(false);
    const [acceptedOrderRef, setAcceptedOrderRef] = useState(null);
    const [error, setError] = useState(null);

    const { data: transfer, isLoading, isError } = useQuery({
        queryKey: ['transfer-status', token],
        queryFn: () => getTransferStatus(token),
    });

    const mutation = useMutation({
        mutationFn: () => acceptTransfer(token),
        onSuccess: (data) => {
            setAccepted(true);
            setAcceptedOrderRef(data.order_ref);
        },
        onError: (err) => setError(err.message),
    });

    // If not authenticated, redirect to login with the transfer URL as next
    useEffect(() => {
        if (!isLoading && !isError && transfer && !transfer.error && !isAuthenticated) {
            const returnUrl = buildPath(`/accept-transfer/${token}`);
            navigate(buildPath(`/login?next=${encodeURIComponent(returnUrl)}`));
        }
    }, [isAuthenticated, transfer, isLoading, isError, token, navigate]);

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('acceptTransfer.loading')} />
            </Dimmer>
        );
    }

    if (isError || !transfer || transfer.error) {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message negative>
                    <Message.Header>{t('acceptTransfer.notFoundTitle')}</Message.Header>
                    <p>{t('acceptTransfer.notFoundBody')}</p>
                </Message>
                <Button basic onClick={() => navigate(buildPath('/events'))}>{t('acceptTransfer.browseEvents')}</Button>
            </Container>
        );
    }

    if (transfer.is_expired || transfer.status === 'expired') {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message warning icon>
                    <Icon name="clock outline" />
                    <Message.Content>
                        <Message.Header>{t('acceptTransfer.expiredTitle')}</Message.Header>
                        <p>{t('acceptTransfer.expiredBody')}</p>
                    </Message.Content>
                </Message>
            </Container>
        );
    }

    if (transfer.status === 'accepted') {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message icon>
                    <Icon name="check circle outline" />
                    <Message.Content>
                        <Message.Header>{t('acceptTransfer.alreadyAcceptedTitle')}</Message.Header>
                        <p>{t('acceptTransfer.alreadyAcceptedBody')}</p>
                    </Message.Content>
                </Message>
                <Button onClick={() => navigate(buildPath('/my-tickets'))}>{t('acceptTransfer.viewMyTickets')}</Button>
            </Container>
        );
    }

    if (transfer.status === 'cancelled') {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message negative>
                    <Message.Header>{t('acceptTransfer.cancelledTitle')}</Message.Header>
                    <p>{t('acceptTransfer.cancelledBody')}</p>
                </Message>
            </Container>
        );
    }

    if (accepted) {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message positive icon>
                    <Icon name="check circle" />
                    <Message.Content>
                        <Message.Header>{t('acceptTransfer.successTitle')}</Message.Header>
                        <p>
                            {t('acceptTransfer.successBody', { eventName: transfer.event_name })}
                        </p>
                    </Message.Content>
                </Message>
                <Button
                    style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff', borderRadius: 8 }}
                    onClick={() => navigate(buildPath(`/my-tickets/${acceptedOrderRef}`))}
                >
                    {t('acceptTransfer.viewMyTicketsButton')}
                </Button>
            </Container>
        );
    }

    // Email mismatch guard (shown when logged in with wrong account)
    const emailMismatch = isAuthenticated && currentUser?.email &&
        currentUser.email.toLowerCase() !== transfer.recipient_email.toLowerCase();

    return (
        <Container className="contentContainer" style={{ maxWidth: 600 }}>
            <Header as="h1" className="sectionHeader">{t('acceptTransfer.title')}</Header>

            <Segment style={{ borderRadius: 12, background: '#f9f9f9', marginBottom: '1.5rem' }}>
                <p style={{ margin: 0 }}>
                    <Icon name="ticket" />
                    <strong>{transfer.event_name}</strong>
                </p>
                <p style={{ margin: '0.3rem 0 0', color: '#555' }}>
                    {transfer.quantity > 1
                        ? t('acceptTransfer.orderSummaryPlural', { orderRef: transfer.order_ref, quantity: transfer.quantity })
                        : t('acceptTransfer.orderSummarySingular', { orderRef: transfer.order_ref, quantity: transfer.quantity })}
                </p>
                <p style={{ margin: '0.3rem 0 0', color: '#555', fontSize: '0.9rem' }}>
                    {t('acceptTransfer.sentTo', { email: transfer.recipient_email })}
                </p>
            </Segment>

            {emailMismatch && (
                <Message warning icon>
                    <Icon name="exclamation triangle" />
                    <Message.Content>
                        <Message.Header>{t('acceptTransfer.wrongAccountTitle')}</Message.Header>
                        <p>
                            {t('acceptTransfer.wrongAccountBody', {
                                currentEmail: currentUser.email,
                                recipientEmail: transfer.recipient_email,
                            })}
                        </p>
                    </Message.Content>
                </Message>
            )}

            {error && (
                <Message negative onDismiss={() => setError(null)}>
                    {error}
                </Message>
            )}

            {!emailMismatch && (
                <>
                    <p style={{ color: '#555', marginBottom: '1.5rem' }}>
                        {t('acceptTransfer.acceptInstructions')}
                    </p>
                    <Button
                        style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff', borderRadius: 8 }}
                        size="large"
                        onClick={() => mutation.mutate()}
                        loading={mutation.isLoading}
                        disabled={mutation.isLoading}
                    >
                        <Icon name="check" />
                        {t('acceptTransfer.acceptButton')}
                    </Button>
                </>
            )}
        </Container>
    );
}

export default AcceptTransfer;
